import { CONSTANTS } from '../constants';
import type { IEnemyLogicState, EnemyType } from './enemy-state';
import { ENEMY_DATA } from './enemy-state';
import type { IPlayerLogicState } from './player-state';

/**
 * Pure game logic for Enemy AI and combat.
 * No Phaser dependencies — used by tests and rendering layer.
 */
export class EnemyLogic {
  private state: IEnemyLogicState;
  private gravity: number;

  constructor(
    initialState: IEnemyLogicState,
    gravity: number = CONSTANTS.GRAVITY
  ) {
    this.state = initialState;
    this.gravity = gravity;
  }

  getState(): IEnemyLogicState {
    return { ...this.state };
  }

  setState(patch: Partial<IEnemyLogicState>): void {
    this.state = { ...this.state, ...patch };
  }

  // --- AI State Machine ---
  updateAI(playerState: IPlayerLogicState, deltaTime: number): void {
    const state = this.state;

    // Update last known player position
    state.lastPlayerX = playerState.x;
    state.lastPlayerY = playerState.y;

    // Handle dead state
    if (state.state === 'dead') {
      state.deathTimer += deltaTime;
      return;
    }

    // Handle stunned state
    if (state.isStunned) {
      state.stunTimer -= deltaTime;
      if (state.stunTimer <= 0) {
        state.isStunned = false;
        // Walk home if the stun happened far from the patrol origin
        state.state = Math.abs(state.x - state.patrolStartX) > state.patrolRange ? 'return' : 'patrol';
      }
      return;
    }

    // Handle attacking state
    if (state.isAttacking) {
      this.updateAttack(deltaTime);
      return;
    }

    // Handle hurt state
    if (state.state === 'hurt') {
      state.state = 'return';
    }

    // Check if player is detected
    const detected = this.checkPlayerDetection(playerState);

    switch (state.state) {
      case 'patrol':
        this.patrol(deltaTime);
        if (detected) {
          state.state = 'detect';
        }
        break;
      case 'detect':
        if (detected) {
          if (this.isPlayerInAttackRange(playerState)) {
            state.state = 'attack';
            this.startAttack();
          } else {
            state.state = 'chase';
          }
        } else {
          state.state = 'return';
        }
        break;
      case 'chase':
        if (!detected) {
          state.state = 'return';
        } else {
          this.chase(playerState, deltaTime);
          if (this.isPlayerInAttackRange(playerState)) {
            state.state = 'attack';
            this.startAttack();
          }
        }
        break;
      case 'return':
        this.returnToPatrol(deltaTime);
        if (detected) {
          state.state = 'detect';
        } else if (this.isBackInPatrolRange()) {
          state.state = 'patrol';
        }
        break;
    }

    // Keep enemies inside the playable world (unless explicitly allowed to leave).
    if (!state.canLeaveWorld && state.worldMaxX > state.worldMinX) {
      this.bounceInWorld();
    }
  }

  private checkPlayerDetection(playerState: IPlayerLogicState): boolean {
    const dx = playerState.x - this.state.x;
    const dy = playerState.y - this.state.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Directional detection: only detect if player is in facing direction
    const inFront = this.state.facing === 'right'
      ? dx > 0
      : dx < 0;

    return dist <= this.state.detectionRange && inFront && Math.abs(dy) < 100;
  }

  private isPlayerInAttackRange(playerState: IPlayerLogicState): boolean {
    const dx = Math.abs(playerState.x - this.state.x);
    if (dx > this.state.attackRange) return false;

    // Vertically the player must overlap the enemy's body (not be jumping over it).
    // The hitbox height is larger than the visible sprite, so require real overlap
    // to avoid registering a hit when the player simply jumps over the enemy.
    const typeData = ENEMY_DATA[this.getType()] || ENEMY_DATA.pcc;
    const halfTotalH = (typeData.height + CONSTANTS.PLAYER_HITBOX_HEIGHT) / 2;
    const playerCenterY = playerState.y - CONSTANTS.PLAYER_HITBOX_HEIGHT / 2;
    const enemyCenterY = this.state.y - typeData.height / 2;
    return Math.abs(playerCenterY - enemyCenterY) <= halfTotalH * 0.8;
  }

  // --- Patrol ---
  private patrol(_deltaTime: number): void {
    const state = this.state;
    const distFromStart = Math.abs(state.x - state.patrolStartX);

    // Too far from home (e.g. after a stun or attack far away): walk back
    // instead of jittering in place with per-frame direction flips.
    if (distFromStart > state.patrolRange) {
      const dir = Math.sign(state.patrolStartX - state.x) || 1;
      this.moveHorizontal(dir, state.speed);
      return;
    }

    // Check edge
    if (distFromStart > state.patrolRange * 0.9) {
      state.patrolDirection = state.x < state.patrolStartX ? 1 : -1;
    }

    this.moveHorizontal(state.patrolDirection, state.speed);
  }

  // Bounce a moving out-of-bounds entity back into the playable world.
  // Used for enemies that must not leave the level (default behavior).
  private bounceInWorld(): void {
    const state = this.state;
    const min = state.worldMinX;
    const max = state.worldMaxX;
    if (state.x <= min) {
      state.x = min;
      state.patrolDirection = 1;
      state.vx = Math.abs(state.vx);
      if (state.facing === 'left') state.facing = 'right';
    } else if (state.x >= max) {
      state.x = max;
      state.patrolDirection = -1;
      state.vx = -Math.abs(state.vx);
      if (state.facing === 'right') state.facing = 'left';
    }
  }

  // --- Chase ---
  private chase(playerState: IPlayerLogicState, _deltaTime: number): void {
    const state = this.state;
    const dx = playerState.x - state.x;
    const direction = dx > 0 ? 1 : -1;

    state.facing = direction > 0 ? 'right' : 'left';
    this.moveHorizontal(direction, state.speed);
  }

  // --- Return to patrol ---
  private returnToPatrol(_deltaTime: number): void {
    const state = this.state;
    const dx = state.patrolStartX - state.x;
    const direction = dx > 0 ? 1 : -1;

    state.facing = direction > 0 ? 'right' : 'left';
    this.moveHorizontal(direction, state.speed * 0.7);
  }

  private isBackInPatrolRange(): boolean {
    const dx = Math.abs(this.state.x - this.state.patrolStartX);
    return dx <= this.state.patrolRange * 0.3;
  }

  // --- Attack ---
  private startAttack(): void {
    const state = this.state;
    state.isAttacking = true;
    state.attackPhase = 'windup';
    state.attackTimer = state.hasBat ? state.attackWindupTime : 0;
  }

  private updateAttack(deltaTime: number): void {
    const state = this.state;

    state.attackTimer -= deltaTime;

    switch (state.attackPhase) {
      case 'windup':
        if (state.attackTimer <= 0) {
          state.attackPhase = 'strike';
          state.attackTimer = state.attackStrikeTime;
        }
        break;
      case 'strike':
        if (state.attackTimer <= 0) {
          state.attackPhase = 'cooldown';
          state.attackTimer = state.attackCooldownTime;
        }
        break;
      case 'cooldown':
        if (state.attackTimer <= 0) {
          state.isAttacking = false;
          // Return home instead of resuming patrol far away
          state.state = Math.abs(state.x - state.patrolStartX) > state.patrolRange ? 'return' : 'patrol';
        }
        break;
    }
  }

  canAttackPlayer(playerState: IPlayerLogicState): boolean {
    const state = this.state;
    if (!state.isAttacking || state.attackPhase !== 'strike') return false;
    return this.isPlayerInAttackRange(playerState);
  }

  getAttackDamage(): number {
    return this.state.damage;
  }

  // --- Physics ---
  updatePhysics(deltaTime: number): void {
    const state = this.state;
    const dt = deltaTime / 16.67;

    if (!state.onGround) {
      state.vy += this.gravity * dt;
      if (state.vy > CONSTANTS.TERMINAL_VELOCITY) {
        state.vy = CONSTANTS.TERMINAL_VELOCITY;
      }
    }

    state.x += state.vx * dt;
    state.y += state.vy * dt;
  }

  private moveHorizontal(direction: number, speed: number): void {
    const state = this.state;
    if (state.vx * direction < speed) {
      state.vx += direction * 0.2;
    }
    if (Math.abs(state.vx) > speed) {
      state.vx = speed * Math.sign(state.vx);
    }
    if (direction !== 0) {
      state.facing = direction > 0 ? 'right' : 'left';
    }
  }

  // --- Damage ---
  takeDamage(amount: number): boolean {
    const state = this.state;
    if (state.state === 'dead') return false;

    state.hp -= amount;
    state.state = 'hurt';
    state.isStunned = true;
    state.stunTimer = 500;

    if (state.hp <= 0) {
      state.hp = 0;
      state.state = 'dead';
      return true;
    }
    return false;
  }

  // --- Helpers ---
  getHitbox(): { x: number; y: number; width: number; height: number } {
    const typeData = ENEMY_DATA[this.state as unknown as EnemyType] || ENEMY_DATA.pcc;
    return {
      x: this.state.x - typeData.width / 2,
      y: this.state.y - typeData.height,
      width: typeData.width,
      height: typeData.height,
    };
  }

  setOnGround(onGround: boolean): void {
    this.state.onGround = onGround;
  }

  getVelocity(): { vx: number; vy: number } {
    return { vx: this.state.vx, vy: this.state.vy };
  }

  isDead(): boolean {
    return this.state.state === 'dead';
  }

  getType(): EnemyType {
    return this.state as unknown as EnemyType;
  }
}
